import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { normalizeRole, type Role } from '@/lib/role';
import { navigateWithoutReload } from '@/lib/navigation';

interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  createdAt?: string;
}

type AuthUserInput = Omit<User, 'role'> & { role: string };

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  login: (token: string, user: AuthUserInput) => void;
  clearAuth: () => void;
  logout: () => void;
  updateUser: (user: AuthUserInput) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,

      login: (token, user) => {
        localStorage.setItem('token', token);
        set({ token, user: normalizeUser(user), isAuthenticated: true });
      },

      clearAuth: () => {
        localStorage.removeItem('token');
        set({ token: null, user: null, isAuthenticated: false });
      },

      logout: () => {
        get().clearAuth();
        navigateWithoutReload('/login');
      },

      updateUser: (user) => {
        set((state) => ({
          user: normalizeUser(user),
          isAuthenticated: Boolean(state.token),
        }));
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state?.token) {
          localStorage.removeItem('token');
          if (state) {
            state.isAuthenticated = false;
          }
          return;
        }

        localStorage.setItem('token', state.token);
        if (state) {
          state.isAuthenticated = Boolean(state.user);
        }
      },
    },
  ),
);

function normalizeUser(user: AuthUserInput): User {
  return {
    ...user,
    role: normalizeRole(user.role),
  };
}
